import React from "react";
import dayjs from "dayjs";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { SortType } from "../../../../../models/SortType";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import { StorageCostListItem } from "../../../../../models/StorageCost/StorageCost";

interface StorageCostListingTableProps {
  rows: StorageCostListItem[];
  sortBy: string;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
  onOpenDetail: (row: StorageCostListItem) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const StorageCostListingTable: React.FC<StorageCostListingTableProps> = ({
  rows,
  sortBy,
  sortType,
  onSortChange,
  onOpenDetail,
}) => {
  const theme = useTheme<Theme>();

  if (rows.length === 0) {
    return (
      <Paper
        sx={{
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ maxHeight: window.innerHeight - 350 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell
                title="Inventory ID"
                sortColumn="id"
                sortBy={sortBy}
                sortDirection={sortType}
                onSortChange={onSortChange}
                sx={{ minWidth: "120px", maxWidth: "120px" }}
              />
              <AppTableHeaderCell title="Customer ID" sx={{ minWidth: "130px", maxWidth: "130px" }} />
              <AppTableHeaderCell title="Inventory Label" sx={{ minWidth: "220px", maxWidth: "300px" }} />
              <AppTableHeaderCell title="Customer Name" sx={{ minWidth: "180px", maxWidth: "240px" }} />
              <AppTableHeaderCell title="Total Cost" sx={{ minWidth: "120px", maxWidth: "150px", whiteSpace: "nowrap" }} />
              <AppTableHeaderCell
                title=" Inventory Created Date"
                sortColumn="created_date"
                sortBy={sortBy}
                sortDirection={sortType}
                onSortChange={onSortChange}
                sx={{ minWidth: "140px", maxWidth: "160px", whiteSpace: "nowrap" }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={`${row.id}-${row.createdDate}`}
                hover
                sx={{
                  "&:nth-of-type(odd)": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <AppTableRowCell
                  sx={{
                    minWidth: "120px",
                    maxWidth: "120px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: "primary.main",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                    onClick={() => onOpenDetail(row)}
                  >
                    {row.id}
                  </Box>
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "130px",
                    maxWidth: "130px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {row.customerId || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "220px",
                    maxWidth: "300px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {row.inventory_label || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "180px",
                    maxWidth: "240px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {row.customerName || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "120px",
                    maxWidth: "150px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatCurrency(row.totalCost)}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "140px",
                    maxWidth: "160px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.createdDate ? dayjs(row.createdDate).format("DD MMM, YYYY") : "-"}
                </AppTableRowCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default StorageCostListingTable;
